import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import styled from '@mui/material/styles/styled';
import Grid from '@mui/material/Grid2';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField/TextField';
import { CircularProgress } from '@mui/material';
import { note, selectedModalDetails, downloadEmail } from '../../slices/invoiceData.slice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { startLoading, stopLoading } from '../../slices/loading.slice';
import CustomTextField from './CustomTextField';
import calanderSVG from '../../assets/imgs/calendar.svg';
import categorySVG from '../../assets/imgs/category.svg';
import chartSVG from '../../assets/imgs/chart.svg';
import clockSVG from '../../assets/imgs/clock.svg';
import closeSVG from '../../assets/imgs/close-square.svg';
import documentSVG from '../../assets/imgs/document.svg';
import documentTextSVG from '../../assets/imgs/document-text.svg';
import dollarSquareSVG from '../../assets/imgs/dollar-square.svg';
import hashSVG from '../../assets/imgs/hash.png';
import infoCircleInfoSVG from '../../assets/imgs/info-circle-info.svg';
import notesSVG from '../../assets/imgs/notes.png';
import personalCardSVG from '../../assets/imgs/personalcard.svg';
import programmingArrowSVG from '../../assets/imgs/programming-arrow.svg';
import receiptDisscountSVG from '../../assets/imgs/receipt-disscount.svg';
import routingSVG from '../../assets/imgs/routing.svg';
import routingWarningSVG from '../../assets/imgs/routing-warning.svg';
import smsGraySVG from '../../assets/imgs/sms-gray.svg';
import smsSVG from '../../assets/imgs/sms.svg';
import smsTrackingSVG from '../../assets/imgs/sms-tracking.svg';
import tickCircleSVG from '../../assets/imgs/tick-circle.svg';
import timekeeperSVG from '../../assets/imgs/timekeeper.png';
import CustomButton from '../Button/Button';
import SendEmailIcon from '../../assets/Icons/SendEmailIcon.svg';
import './modal.scss';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '40%',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '12px',
  p: 4,
};

type InvoiceType = {
  [key: string]: string
};

type InvoicePropType = {
  open: boolean;
  handleClose: () => void;
  handleSendEmail: () => void;
  handleOpenInvoice: (url: string) => void;
};

const StyledTextArea = styled(TextField)({
  "& .MuiInputBase-root": {
    borderRadius: '10px'
  }
});

const formatDate = (inputDate: string) => {
  const date = new Date(inputDate);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear() % 100;
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  return `${month}/${day}/${year} ${hours}:${minutes}:${seconds} ${ampm}`;
}
const convertToCustomFormat = (isoDate: string) => {
  const date = new Date(isoDate);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

const checkError = (value: string) => value === 'Error';


const Invoice = ({ open, handleClose, handleSendEmail, handleOpenInvoice }: InvoicePropType) => {
  const dispatch = useAppDispatch();
  const modalDetails = useAppSelector(selectedModalDetails) || [];
  const [userNote, setUserNote] = useState('');

  const isLoading = useAppSelector((state) => state.loading.isLoading);

  const { notes = [], id: arDashboardId } = modalDetails;

  const {
    botStatus = '',
    botComment = '',
    processStatus = '',
    template = '',
    emailSentToClient = '',
    softCost = '',
    timestamp = '',
    clientName = '',
    collector = '',
    invoiceDate = '',
    practiceGroup = '',
    matterType = '',
    terms = '',
    outstandingBalance = '',
    total = '',
    fees = '',
    hardCost = '',
    unallocated = '',
    matterNumber = '',
    originalBDTimekeeperName = '',
    matterBDTimekeeperName = '',
    invoice = '',
    sharePointInvoicePath = '',
    sharePointEmailPath = '',
    id = ''
  }: InvoiceType = modalDetails;


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserNote(e.target.value);
  }

  const handleSubmit = () => {
    dispatch(startLoading());

    dispatch(note({ arDashboardId, message: userNote }))
      .then(() => {
        setUserNote('');
      })
      .finally(() => {
        dispatch(stopLoading());
      })
  }

  const handleDownloadEmail = () => {
    dispatch(startLoading());
    dispatch(downloadEmail(id))
      .finally(() => {
        dispatch(stopLoading());
      })
  }


  return (
    <Modal open={open} className='InvoiceModal'>
      <Box sx={{ flexGrow: 1, ...modalStyle }}>
        {isLoading && (
          <div className='loader-container'>
            <div className='loader-overlay'>
              <CircularProgress />
            </div>
          </div>
        )}
        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }} className="title">
            {invoice}
          </Grid>
          <Grid size={{ xs: 6 }} sx={{ textAlign: 'right', cursor: 'pointer' }}>
            <img src={closeSVG} onClick={handleClose} />
          </Grid>
        </Grid>
        <hr />
        <div className="scrollable">
          <Grid container spacing={2} className='btnContainer'>
            <Grid size={{ xs: 6 }} sx={{ textAlign: 'center' }} className="left-button">
              <Button variant="contained" className="btn-primary left-button" endIcon={<img src={documentTextSVG} alt="Document Icon" />} onClick={() => handleOpenInvoice(sharePointInvoicePath)} disabled={!sharePointInvoicePath}>
                Open Invoice
              </Button>
            </Grid>
            <Grid size={{ xs: 6 }} sx={{ textAlign: 'center' }} className="right-button">
              <Button variant="contained" className="btn-primary" endIcon={<img src={smsSVG} />} onClick={handleDownloadEmail} disabled={!sharePointEmailPath}>
                Open Email
              </Button>
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ padding: '30px 0px' }}>
            <Grid size={{ xs: 6 }} sx={{ textAlign: 'left' }}>
              <img src={chartSVG} className="middle-title" /><span style={{ fontSize: '22px', fontWeight: '600', fontFamily: 'Poppins, sans-serif' }}>Status</span>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Bot Status"
                icon={() => {
                  if (checkError(botStatus)) return <img src={infoCircleInfoSVG} />
                  else return <img src={tickCircleSVG} />
                }}
                variant="outlined"
                isWarning={checkError(botStatus)}
                value={botStatus}
              />
            </Grid>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Bot Comment"
                icon={() => {
                  if (checkError(botStatus)) return <img src={infoCircleInfoSVG} />
                  else return <img src={tickCircleSVG} />
                }}
                variant="outlined"
                isWarning={checkError(botStatus)}
                value={botComment}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Process Status"
                icon={() => {
                  if (checkError(botStatus)) return <img src={routingWarningSVG} />
                  else return <img src={routingSVG} />
                }}
                isWarning={checkError(botStatus)}
                variant="outlined"
                value={processStatus}
              />
            </Grid>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Template"
                icon={() => <img src={smsGraySVG} />}
                variant="outlined"
                value={template}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Email Sent to Client"
                icon={() => <img src={smsTrackingSVG} />}
                variant="outlined"
                value={emailSentToClient}
              />
            </Grid>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Timestamp"
                icon={() => <img src={clockSVG} />}
                variant="outlined"
                value={formatDate(timestamp)}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className="middle-parent-head">
            <Grid size={{ xs: 6 }} sx={{ textAlign: 'left', margin: '40px 0 20px', }}>
              <img src={documentSVG} className="middle-title" /><span style={{ fontSize: '22px', fontWeight: '600', fontFamily: 'Poppins, sans-serif' }}>Invoice Details</span>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Client Name"
                icon={() => <img src={personalCardSVG} />}
                variant="outlined"
                value={clientName}
              />
            </Grid>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Collector Name"
                icon={() => <img src={routingSVG} />}
                variant="outlined"
                value={collector}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="invoice Date"
                icon={() => <img src={calanderSVG} />}
                variant="outlined"
                value={convertToCustomFormat(invoiceDate)}
              />
            </Grid>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Practice Group"
                icon={() => <img src={categorySVG} />}
                variant="outlined"
                value={practiceGroup}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Matter Type"
                icon={() => <img src={programmingArrowSVG} />}
                variant="outlined"
                value={matterType}
              />
            </Grid>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Terms"
                icon={() => <img src={receiptDisscountSVG} />}
                variant="outlined"
                value={terms}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Outstanding Balace"
                icon={() => <img src={dollarSquareSVG} />}
                variant="outlined"
                value={`$${outstandingBalance === '' ? ' -' : parseFloat(outstandingBalance).toLocaleString()}`}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className="middle-parent-head">
            <Grid size={{ xs: 6 }} sx={{ textAlign: 'left', margin: '40px 0 20px', }}>
              <img src={dollarSquareSVG} style={{ position: 'relative', top: '5px', paddingRight: '10px' }} /><span style={{ fontWeight: '600', fontFamily: 'Poppins, sans-serif', fontSize: '22px' }}>Fee Details</span>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Total"
                icon={() => <img src={dollarSquareSVG} />}
                variant="outlined"
                value={`$${total === '' ? ' -' : parseFloat(total).toLocaleString()}`}
              />
            </Grid>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Fees"
                icon={() => <img src={dollarSquareSVG} />}
                variant="outlined"
                value={`$${fees === '' ? ' -' : parseFloat(fees).toLocaleString()}`}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Hard Cost"
                icon={() => <img src={dollarSquareSVG} />}
                variant="outlined"
                value={`$${hardCost === '' ? ' -' : parseFloat(hardCost).toLocaleString()}`}
              />
            </Grid>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Soft Cost"
                icon={() => <img src={dollarSquareSVG} />}
                variant="outlined"
                value={`$${softCost === '' ? ' -' : parseFloat(softCost).toLocaleString()}`}

              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Unallocated"
                icon={() => <img src={dollarSquareSVG} />}
                variant="outlined"
                value={`$${unallocated === '' ? ' -' : unallocated}`}

              />
            </Grid>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Matter Number"
                icon={() => <img src={hashSVG} style={{ width: '20px', height: '20px' }} />}
                variant="outlined"
                value={matterNumber}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Original BD Timekeeper"
                icon={() => <img src={timekeeperSVG} />}
                variant="outlined"
                value={originalBDTimekeeperName}
              />
            </Grid>
            <Grid size={{ xs: 6 }} className="parent-gride-text">
              <CustomTextField
                label="Matter BD Timekeeper"
                icon={() => <img src={timekeeperSVG} />}
                variant="outlined"
                value={matterBDTimekeeperName}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className="middle-parent-head" style={{ margin: '40px 0 20px' }}>
            <Grid size={{ xs: 6 }} sx={{ textAlign: 'left' }}>
              <img src={notesSVG} style={{ position: 'relative', top: '5px', paddingRight: '10px' }} /><span style={{ fontSize: '22px', fontWeight: '600', fontFamily: 'Poppins, sans-serif' }}>Notes</span>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <div className='textareaContainer' >
                <StyledTextArea
                  multiline
                  rows={6}
                  variant="outlined"
                  label="Add a note..."
                  fullWidth
                  value={userNote}
                  onChange={handleInputChange}
                />

              </div>
              <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'right' }}>
                <Button className="btn-primary submit" onClick={handleSubmit} disabled={!userNote.trim()}>
                  Submit
                </Button>
              </Grid>
              {(notes.length > 0) &&
                notes.map((note: any, index: any) => {
                  const {
                    createdBy = '',
                    message = '',
                    createdDate = '',
                  } = note;
                  return (
                    <Grid size={{ xs: 12 }} className="note-message" key={index}>
                      <h4>{createdBy}</h4>
                      <p>{message}</p>
                      <span>{formatDate(createdDate)}</span>
                      <hr />
                    </Grid>
                  )
                })
              }
            </Grid>
          </Grid>
          <Grid className="sendEmailBtnContainer">
            <CustomButton onClick={handleSendEmail}>
              Log Email Send
              <img src={SendEmailIcon} alt={"Icon"} />
            </CustomButton>
          </Grid>
        </div>

      </Box>

    </Modal>
  );
};

export default Invoice;
